<script setup lang="ts">
import { when, type RunSummary } from "../api";
import Icon from "./Icon.vue";
import { href } from "../router";
import { modeText, runPill } from "../status";

withDefaults(defineProps<{ runs: RunSummary[]; compact?: boolean }>(), { compact: false });
</script>

<template>
  <div class="list">
    <a v-for="r in runs" :key="r.runId" class="run" :href="href('naprawy', r.runId)">
      <div class="ic" :class="runPill(r).cls">
        <span v-if="r.status === 'running'" class="spin" />
        <Icon v-else-if="r.status === 'failed'" name="x" :size="18" />
        <Icon v-else-if="r.status === 'warning'" name="alert" :size="18" />
        <Icon v-else name="check" :size="18" />
      </div>
      <div class="grow">
        <div class="top">
          <span class="domain">{{ r.domain }}</span>
          <span class="pill" :class="runPill(r).cls">{{ runPill(r).text }}</span>
          <span v-if="r.feedback === 'ok'" class="pill ok">potwierdzono</span>
          <span v-else-if="r.feedback === 'broken'" class="pill crit">zgłoszony problem</span>
          <span v-else-if="r.mode === 'quarantine' && r.status !== 'running' && r.status !== 'failed'" class="pill acc">do potwierdzenia</span>
        </div>
        <div class="meta">
          {{ when(r.createdAt) }} • {{ modeText(r.mode) }}
          <template v-if="!compact"> • <span class="mono">{{ r.target }}</span></template>
        </div>
      </div>
      <div class="nums">
        <div v-if="r.threats !== null" class="num"><b :class="{ hot: r.threats > 0 }">{{ r.threats }}</b><span>zagrożeń</span></div>
        <div v-if="r.actions || r.progress?.actions.quarantined" class="num"><b>{{ r.progress?.actions.quarantined ?? r.quarantined }}</b><span>kwarantanna</span></div>
        <div v-if="r.actions || r.progress?.actions.repaired" class="num"><b>{{ r.progress?.actions.repaired ?? r.repaired }}</b><span>naprawione</span></div>
      </div>
      <div class="arrow"><Icon name="chevron-right" :size="20" /></div>
    </a>
  </div>
</template>

<style scoped>
.list { display: flex; flex-direction: column; gap: var(--s2); }
.run {
  display: flex; align-items: center; gap: var(--s3); text-decoration: none; color: inherit;
  border: 1px solid var(--bd); border-radius: var(--r2); padding: var(--s3) var(--s4);
  background: var(--panel); transition: border-color .12s, transform .12s, box-shadow .12s;
}
.run:hover { border-color: var(--acc); box-shadow: var(--shadow); transform: translateY(-1px); }
.ic {
  width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; flex: 0 0 auto;
  font-weight: 800; background: var(--panel3); color: var(--mut);
}
.ic.ok { background: var(--ok-dim); color: var(--ok); }
.ic.warn { background: var(--warn-dim); color: var(--warn); }
.ic.crit { background: var(--crit-dim); color: var(--crit); }
.ic.acc { background: var(--acc-dim); color: var(--acc); }
.spin { width: 14px; height: 14px; border: 2px solid var(--acc); border-right-color: transparent; border-radius: 50%; animation: rot .8s linear infinite; }
@keyframes rot { to { transform: rotate(360deg); } }
.top { display: flex; align-items: center; gap: var(--s2); flex-wrap: wrap; }
.domain { font-weight: 650; font-size: 14px; }
.meta { font-size: 12px; color: var(--mut); margin-top: 2px; word-break: break-all; }
.nums { display: flex; gap: var(--s4); }
.num { display: flex; flex-direction: column; align-items: center; min-width: 56px; }
.num b { font-size: 16px; }
.num span { font-size: 10px; color: var(--mut2); text-transform: uppercase; letter-spacing: .04em; }
.hot { color: var(--crit); }
.arrow { color: var(--mut2); font-size: 22px; }
@media (max-width: 640px) { .nums { display: none; } }
</style>
